import { v4 as uuidv4 } from 'uuid';

import type * as bookingTypes from 'src/types/booking';
import type * as localeTypes from 'src/types/locale';
import type * as paymentTypes from 'src/types/payment';

export function activateUser(): void {
  localStorage.setItem('userStatus', 'active');
}

export function clearPendingFlightRequest(): void {
  sessionStorage.removeItem('pendingFlightRequest');
}

export function clearPackages(): void {
  localStorage.removeItem('packages');
}

export function clearPendingVisaRequest(): void {
  sessionStorage.removeItem('pendingVisaRequest');
}

export function clearRedirectAfterAuth(): void {
  sessionStorage.removeItem('redirectAfterAuth');
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

export function getAppReviewLastPromptedAt(): string | null {
  return localStorage.getItem('appReviewLastPromptedAt');
}

// @note Correlation id shared across a single search → hotel page → book lifecycle. Created
// lazily so a deep-linked hotel page and its booking still share one id (see startBookingLifecycle).
export function getBookingCorrelationId(): string {
  let correlationId = sessionStorage.getItem('bookingCorrelationId');
  if (!correlationId) {
    correlationId = uuidv4();
    sessionStorage.setItem('bookingCorrelationId', correlationId);
  }
  return correlationId;
}

export function getBookingSelection(): bookingTypes.BookingSelection | null {
  const stored = sessionStorage.getItem('bookingSelection');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as bookingTypes.BookingSelection;
  } catch {
    return null;
  }
}

export function getBookingToken(): string | null {
  return sessionStorage.getItem('bookingToken');
}

export function getPendingFlightRequest(): string | null {
  return sessionStorage.getItem('pendingFlightRequest');
}

export function getPendingVisaRequest(): string | null {
  return sessionStorage.getItem('pendingVisaRequest');
}

export function getLocale(): localeTypes.Locale {
  const stored = localStorage.getItem('locale');
  if (stored === 'en-US' || stored === 'ar') return stored;
  return navigator.language.startsWith('ar') ? 'ar' : 'en-US';
}

// @note Membership packages captured at login/register so an inactive user can be sent straight to
// the activation payment page later (from the calculator or a request flow) without another API call.
export function getPackages(): paymentTypes.Package[] {
  const stored = localStorage.getItem('packages');
  if (!stored) return [];
  try {
    return JSON.parse(stored) as paymentTypes.Package[];
  } catch {
    return [];
  }
}

export function getRedirectAfterAuth(): string | null {
  return sessionStorage.getItem('redirectAfterAuth');
}

export function getUserId(): string | null {
  return localStorage.getItem('userId');
}

export function getUserStatus(): string | null {
  return localStorage.getItem('userStatus');
}

export function isLoggedIn(): boolean {
  return !!getAccessToken();
}

// @note Active member = logged in AND status 'active'. `userStatus` is a non-reactive localStorage
// value set at login and by activateUser() after the activation payment.
export function isUserActive(): boolean {
  return isLoggedIn() && getUserStatus() === 'active';
}

export function isUserComeFromApp(): boolean {
  return localStorage.getItem('userComeFromApp') === 'true';
}

export function login({
  status,
  token,
  userId,
}: {
  status: string;
  token: string;
  userId: string;
}): void {
  if (token) {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('userId', userId);
    if (status) {
      localStorage.setItem('userStatus', status);
    }
  }
}

// @note `preserveReturnPath` is set only for involuntary logouts (session expiry / 401 mid-navigation)
// so the user lands back where they were headed after re-login. A user-initiated logout passes nothing
// and always returns to /login.
export function logOut({ preserveReturnPath }: { preserveReturnPath?: boolean } = {}): void {
  // @note Preserve locale across logout — language preference outlives auth.
  const locale = localStorage.getItem('locale');

  // If the guard already stashed a return path, keep it; otherwise fall back to the current location,
  // unless we're already on an auth page (login/otp/register) — those must never be a return target.
  const authPaths = ['/login', '/otp', '/register'];
  const currentPath = window.location.pathname + window.location.search;
  const redirectAfterAuth = preserveReturnPath
    ? (sessionStorage.getItem('redirectAfterAuth') ??
      (authPaths.includes(window.location.pathname) ? null : currentPath))
    : null;

  localStorage.clear();
  sessionStorage.clear();
  if (locale) {
    localStorage.setItem('locale', locale);
  }
  if (redirectAfterAuth) {
    sessionStorage.setItem('redirectAfterAuth', redirectAfterAuth);
  }
  window.location.href = '/login';
}

export function setAppReviewLastPromptedAt(timestamp: string): void {
  localStorage.setItem('appReviewLastPromptedAt', timestamp);
}

export function setBookingSelection(selection: bookingTypes.BookingSelection): void {
  sessionStorage.setItem('bookingSelection', JSON.stringify(selection));
}

export function setBookingToken(token: string): void {
  sessionStorage.setItem('bookingToken', token);
}

export function setLocale(locale: localeTypes.Locale): void {
  localStorage.setItem('locale', locale);
}

export function setPackages(packages: paymentTypes.Package[]): void {
  localStorage.setItem('packages', JSON.stringify(packages));
}

export function setPendingFlightRequest(payload: string): void {
  sessionStorage.setItem('pendingFlightRequest', payload);
}

export function setPendingVisaRequest(payload: string): void {
  sessionStorage.setItem('pendingVisaRequest', payload);
}

export function setRedirectAfterAuth(path: string): void {
  sessionStorage.setItem('redirectAfterAuth', path);
}

export function setUserComeFromApp() {
  localStorage.setItem('userComeFromApp', 'true');
}

// @note A new search opens a fresh correlation lifecycle (search → hotel page → book share this
// id) and clears the previous search's token and rate selection.
export function startBookingLifecycle(): string {
  const correlationId = uuidv4();
  sessionStorage.setItem('bookingCorrelationId', correlationId);
  sessionStorage.removeItem('bookingSelection');
  sessionStorage.removeItem('bookingToken');
  return correlationId;
}
